import React, { createContext, forwardRef, useContext } from "react";
import { Popover as HeadlessPopover, Transition } from "@headlessui/react";
import { cn } from "./cn";
import FloatingPanel from "./FloatingPanel";

const PopoverContext = createContext({ open: false });

export default function Popover({ as = "div", className, children, ...props }) {
  // The group includes Headless UI's trigger focus guard, so crossing into a
  // portalled panel does not look like focus has left the popover.
  return (
    <HeadlessPopover.Group as={React.Fragment}>
      <HeadlessPopover as={as} className={cn("relative font-instrument", className)} {...props}>
        {(state) => (
          <PopoverContext.Provider value={state}>
            {typeof children === "function" ? children(state) : children}
          </PopoverContext.Provider>
        )}
      </HeadlessPopover>
    </HeadlessPopover.Group>
  );
}

Popover.Button = HeadlessPopover.Button;

const Panel = forwardRef(function Panel(
  { align = "left", width, portal = false, anchorRef, maxHeight, className, children, ...props },
  ref
) {
  const state = useContext(PopoverContext);
  const { open } = state;
  if (portal) {
    return (
      <Transition
        show={open}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <FloatingPanel
          as={HeadlessPopover.Panel}
          data-bun-popover-panel=""
          static
          open
          ref={ref}
          anchorRef={anchorRef}
          align={align === "left" ? "start" : align === "right" ? "end" : align}
          width={width}
          maxHeight={maxHeight}
          portalTarget={anchorRef?.current?.closest('[data-bun-dialog-panel], [role="dialog"]')}
          className={cn("p-1.5 focus:outline-none", className)}
          {...props}
        >
          {typeof children === "function" ? children(state) : children}
        </FloatingPanel>
      </Transition>
    );
  }
  return (
    <Transition
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 -translate-y-1"
      enterTo="transform opacity-100 translate-y-0"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 translate-y-0"
      leaveTo="transform opacity-0 -translate-y-1"
    >
      <HeadlessPopover.Panel
        data-bun-popover-panel=""
        ref={ref}
        style={{ width }}
        className={cn(
          "absolute z-50 mt-2 overflow-hidden rounded-bun-lg border border-bun-line bg-bun-surface p-1.5 font-instrument text-bun-text shadow-bun-menu focus:outline-none",
          align === "right" ? "right-0" : "left-0",
          className
        )}
        {...props}
      >
        {children}
      </HeadlessPopover.Panel>
    </Transition>
  );
});

function Option({ selected = false, className, children, ...props }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "flex min-h-11 sm:min-h-9 w-full items-center gap-3 rounded-[8px] px-2.5 text-left text-bun-meta font-book transition-colors",
        "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue",
        selected
          ? "bg-bun-mist-deep font-strong text-bun-text"
          : "text-bun-slate hover:bg-bun-mist hover:text-bun-text",
        className
        )}
      {...props}
    >
      {children}
    </button>
  );
}

Popover.Panel = Panel;
Popover.Option = Option;
